import OffersService from '~/services/OffersService';
import { ProductOfflineImpact, getProductOfflineImpact } from '~/services/OrdersService';
import ReservationService from '~/services/ReservationService';

const PAGE_LIMIT = 100;
const SAMPLE_LIMIT = 10;

interface Property {
  room_types?: Array<{ room_rates?: Array<{ id: string; rate_plan?: { id: string } }> }>;
}

// Do not filter offers by their current status or package configuration: existing bookings
// retain the room-rate ID in their purchase snapshot even after a campaign ends or changes.
export async function getRatePlanBookingImpact(vendorId: string, ratePlanId: string): Promise<ProductOfflineImpact> {
  const roomRateIds = new Set<string>();
  for (let page = 1; ; page++) {
    const response = await ReservationService.getProperties(vendorId, PAGE_LIMIT, page, true);
    const properties: Array<Property> = response.result;
    for (const property of properties) {
      for (const roomType of property.room_types ?? []) {
        for (const roomRate of roomType.room_rates ?? []) {
          if (roomRate.rate_plan?.id === ratePlanId) roomRateIds.add(roomRate.id);
        }
      }
    }
    if (properties.length < PAGE_LIMIT) break;
  }

  const impact: ProductOfflineImpact = {
    awaitingDates: { total: 0, sample: [] },
    upcomingTrips: { total: 0, sample: [] },
  };
  // Never send an empty scope: the API would count every booking on the offer.
  if (!roomRateIds.size) return impact;

  const offerIds = new Set<string>();
  for (let page = 1; ; page++) {
    const response = await OffersService.getOffers({ queryString: vendorId, limit: PAGE_LIMIT, page });
    for (const offer of response.result) offerIds.add(offer.id_salesforce_external);
    if (response.result.length < PAGE_LIMIT) break;
  }

  // Serial requests bound load on Orders when a vendor has many historical campaigns.
  for (const offerId of offerIds) {
    const result = await getProductOfflineImpact({ offerId, roomRateIds: [...roomRateIds] });
    for (const bucket of ['awaitingDates', 'upcomingTrips'] as const) {
      impact[bucket].total += result[bucket].total;
      impact[bucket].sample = [...impact[bucket].sample, ...result[bucket].sample].slice(0, SAMPLE_LIMIT);
    }
  }
  return impact;
}
